import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArticleCategory,
  ArticleTemplate,
  ArticleVisibility,
  ArticleStatus,
  type ArticleInput,
  type PublicArticle,
} from "@vedaunion/shared";
import { BlockEditor } from "../../components/editor/BlockEditor";
import { articleEditorConfig } from "../../components/editor/articleConfig";
import { ensureDoc, emptyDoc, toHtml as docToHtml, toExcerpt } from "../../components/editor/utils";
import type { BlockDoc } from "../../components/editor/types";
import { TEMPLATES, findTemplate } from "../../components/article/templates";
import { RevisionDrawer } from "../../components/article/RevisionDrawer";
import {
  useArticle,
  useCreateArticle,
  useUpdateArticle,
  useDeleteArticle,
  useTagSuggestions,
} from "../../lib/articles";
import { useEvents } from "../../lib/events";
import { useCan, useMe } from "../../lib/auth";
import { ApiError } from "../../lib/api";
import { format } from "date-fns";

/**
 * Create/edit page for a single article. The editor takes most of the screen;
 * a slim sidebar holds metadata (title, category, status, etc.) plus the
 * template picker (only shown on create). Saves to /api/articles via a debounced
 * mutation when the user clicks Save.
 */

const STATUS_OPTIONS = ArticleStatus.options;
const VIS_OPTIONS = ArticleVisibility.options;
const CAT_OPTIONS = ArticleCategory.options;
const TPL_OPTIONS = ArticleTemplate.options;

interface MetaState {
  title: string;
  subtitle: string;
  excerpt: string;
  coverImage: string;
  category: typeof CAT_OPTIONS[number];
  templateKey: typeof TPL_OPTIONS[number];
  status: typeof STATUS_OPTIONS[number];
  visibility: typeof VIS_OPTIONS[number];
  featured: boolean;
  tags: string;
  relatedEventId: number | null;
  revisionNote: string;
  /** Local datetime-input string, e.g. "2026-04-30T18:30". Empty = let server pick. */
  publishedAt: string;
}

const DEFAULT_META: MetaState = {
  title: "",
  subtitle: "",
  excerpt: "",
  coverImage: "",
  category: "news",
  templateKey: "blank",
  status: "draft",
  visibility: "public",
  featured: false,
  tags: "",
  relatedEventId: null,
  revisionNote: "",
  publishedAt: "",
};

/** Convert an ISO datetime string from the server into the local `datetime-local`
 *  input format ("YYYY-MM-DDTHH:mm") preserving the user's timezone. */
function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Convert a local datetime-local input value back to an ISO string in UTC. */
function fromDatetimeLocalValue(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function ArticleForm({ mode }: { mode: "create" | "edit" }) {
  const navigate = useNavigate();
  const params = useParams<{ slug: string }>();
  const me = useMe();
  const canPublish = useCan("articles.publish");
  const canFeature = useCan("articles.feature");
  const canEditAny = useCan("articles.edit_any");
  const canDelete = useCan("articles.delete_any");

  const articleQ = useArticle(mode === "edit" ? params.slug : undefined);
  const article = articleQ.data?.article ?? null;
  const create = useCreateArticle();
  const update = useUpdateArticle(article?.id ?? 0);
  const remove = useDeleteArticle();

  // Pull a list of events the user can choose from. We include 'all' for
  // editors so drafts can be linked too; otherwise only published.
  const eventsQ = useEvents({ all: canEditAny, limit: 200 });
  const eventOptions = eventsQ.data?.events ?? [];

  /** Interaction flag — flips true on any user-driven setMeta/setDoc and
   *  resets to false after each successful save / hydration. The auto-save
   *  effect skips its debounce timer when this is false so reading an
   *  article doesn't bump updatedAt on every load. */
  const hasInteractedRef = useRef(false);
  const [meta, setMetaRaw] = useState<MetaState>(DEFAULT_META);
  const [doc, setDocRaw] = useState<BlockDoc>(() => emptyDoc());
  /** Interactive setters mark the ref so the next debounced auto-save fires.
   *  The hydration effect uses the raw setters so reading an article doesn't
   *  trip the auto-save into thinking the user has typed. */
  const setMeta = (updater: MetaState | ((prev: MetaState) => MetaState)) => {
    hasInteractedRef.current = true;
    setMetaRaw(updater);
  };
  const setDoc = (next: BlockDoc) => {
    hasInteractedRef.current = true;
    setDocRaw(next);
  };
  const [error, setError] = useState<string | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(mode === "create");
  const [showRevisions, setShowRevisions] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const tagSuggestionsQ = useTagSuggestions();
  const tagSuggestions = tagSuggestionsQ.data?.tags ?? [];

  // Hydrate when editing — uses raw setters so the interaction flag stays
  // false; the user's first real keystroke is the trigger for auto-save.
  useEffect(() => {
    if (mode !== "edit" || !article) return;
    setMetaRaw({
      title: article.title,
      subtitle: article.subtitle ?? "",
      excerpt: article.excerpt ?? "",
      coverImage: article.coverImage ?? "",
      category: article.category,
      templateKey: article.templateKey,
      status: article.status,
      visibility: article.visibility,
      featured: article.featured,
      tags: (article.tags ?? []).join(", "),
      relatedEventId: article.relatedEventId,
      revisionNote: "",
      publishedAt: toDatetimeLocalValue(article.publishedAt),
    });
    setDocRaw(ensureDoc(article.contentJson));
    setShowTemplatePicker(false);
    hasInteractedRef.current = false;
  }, [mode, article]);

  const isOwn = !!me.data?.user && article?.authorId === me.data.user.id;
  const canEditThis = mode === "create" || isOwn || canEditAny;

  const allowedStatuses = useMemo(() => {
    const set = new Set<typeof STATUS_OPTIONS[number]>(["draft", "review", "archived"]);
    if (canPublish || article?.status === "published") set.add("published");
    return STATUS_OPTIONS.filter((s) => set.has(s));
  }, [canPublish, article?.status]);

  const onPickTemplate = (key: typeof TPL_OPTIONS[number]) => {
    const tpl = findTemplate(key);
    setMeta((m) => ({
      ...m,
      templateKey: key,
      // Don't auto-fill the title from the Blank "label" — Blank should leave
      // the title empty so the author starts from a clean canvas.
      title: m.title || (key === "blank" ? "" : tpl.label),
    }));
    setDoc(structuredClone(tpl.doc));
    setShowTemplatePicker(false);
  };

  const onSave = async () => {
    setError(null);
    if (!meta.title.trim()) {
      setError("Title is required.");
      return;
    }
    const html = docToHtml(doc, articleEditorConfig);
    const autoExcerpt = meta.excerpt.trim() || toExcerpt(doc, articleEditorConfig);
    const payload: ArticleInput = {
      title: meta.title.trim(),
      subtitle: meta.subtitle.trim() || null,
      excerpt: autoExcerpt || null,
      coverImage: meta.coverImage.trim() || null,
      contentJson: doc,
      contentHtml: html,
      templateKey: meta.templateKey,
      category: meta.category,
      tags: meta.tags
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter((t) => t.length > 0)
        .slice(0, 20),
      status: meta.status,
      visibility: meta.visibility,
      featured: meta.featured,
      relatedEventId: meta.relatedEventId,
      revisionNote: meta.revisionNote.trim() || null,
      // Only send `publishedAt` if the user has edit-published rights — and
      // even then, only when the field is non-empty. The server defaults to
      // NOW on first publish when this is omitted.
      ...(canPublish && meta.publishedAt
        ? { publishedAt: fromDatetimeLocalValue(meta.publishedAt) }
        : {}),
    };

    try {
      let saved: { article: PublicArticle };
      if (mode === "create") {
        saved = await create.mutateAsync(payload);
      } else if (article) {
        saved = await update.mutateAsync(payload);
      } else {
        return;
      }
      setSavedAt(new Date());
      navigate(`/dashboard/articles/${saved.article.slug}`);
    } catch (err) {
      const e = err as ApiError;
      setError(e?.body?.message ?? e?.message ?? "Save failed.");
    }
  };

  /** Auto-save for drafts. Skips published articles (they require a revision
   *  note + explicit Save). Skips create mode (no article id yet). 4s debounce.
   *  Also skips the run that fires immediately after hydration so we don't
   *  bump `updated_at` on every read. */
  useEffect(() => {
    if (mode !== "edit" || !article) return;
    // Only auto-save when both the saved + local state agree we're on a draft.
    // Pending status changes (draft → review, published → draft, etc.) need
    // an explicit Save so the user sees the transition succeed/fail clearly.
    if (article.status !== "draft" || meta.status !== "draft") return;
    if (!canEditThis) return;
    if (!hasInteractedRef.current) return;
    const timer = window.setTimeout(async () => {
      try {
        const html = docToHtml(doc, articleEditorConfig);
        await update.mutateAsync({
          title: meta.title.trim() || article.title,
          subtitle: meta.subtitle.trim() || null,
          excerpt: meta.excerpt.trim() || null,
          coverImage: meta.coverImage.trim() || null,
          contentJson: doc,
          contentHtml: html,
          templateKey: meta.templateKey,
          category: meta.category,
          tags: meta.tags
            .split(",")
            .map((t) => t.trim().toLowerCase())
            .filter((t) => t.length > 0)
            .slice(0, 20),
          visibility: meta.visibility,
          relatedEventId: meta.relatedEventId,
        });
        setSavedAt(new Date());
        hasInteractedRef.current = false;
      } catch {
        // Silent — the explicit Save button surfaces failures clearly.
      }
    }, 4000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, meta.title, meta.subtitle, meta.excerpt, meta.coverImage, meta.tags, meta.category, meta.visibility, meta.relatedEventId, meta.templateKey, meta.status, mode, article?.id]);

  const onRestoreRevision = (versionNumber: number, restoredDoc: BlockDoc, restoredHtml: string) => {
    setDoc(restoredDoc);
    setMeta((m) => ({
      ...m,
      revisionNote: m.revisionNote || `Restored from v${versionNumber}`,
    }));
    setShowRevisions(false);
    // Use the html as the immediate snapshot too so the next save persists it.
    void restoredHtml;
  };

  const onPreview = () => {
    if (mode === "create") return;
    if (!article) return;
    window.open(`/news/${article.slug}?preview=1`, "_blank", "noopener");
  };

  const onDelete = async () => {
    if (!article) return;
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await remove.mutateAsync(article.id);
      navigate("/dashboard/articles");
    } catch (err) {
      const e = err as ApiError;
      window.alert(e?.body?.message ?? e?.message ?? "Delete failed.");
    }
  };

  if (mode === "edit" && articleQ.isLoading) return <p className="container-edit">Loading…</p>;
  if (mode === "edit" && !article) return <p className="container-edit">Article not found.</p>;
  if (mode === "edit" && !canEditThis) return <p className="container-edit">You don't have permission to edit this article.</p>;

  const savedAgo = savedAt ? Math.max(1, Math.round((Date.now() - savedAt.getTime()) / 1000)) : null;

  return (
    <div className="container-edit max-w-[1280px]">
      <header className="flex items-baseline justify-between gap-3 flex-wrap mb-6">
        <div>
          <p className="page-eyebrow">{mode === "create" ? "New article" : "Edit article"}</p>
          <h1 className="page-title">
            {mode === "create" ? "Begin a new piece." : article?.title}
          </h1>
        </div>
        <div className="hidden md:flex flex-wrap gap-3 items-center">
          {savedAgo !== null && (
            <span className="font-grotesk text-[0.66rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
              Saved {savedAgo < 60 ? `${savedAgo}s ago` : `${Math.round(savedAgo / 60)}m ago`}
            </span>
          )}
          <button type="button" className="btn-app-ghost" onClick={() => navigate(-1)}>
            Cancel
          </button>
          {mode === "edit" && (
            <>
              <button type="button" className="btn-app-ghost" onClick={onPreview}>
                Preview
              </button>
              <button
                type="button"
                className="btn-app-ghost"
                onClick={() => setShowRevisions(true)}
              >
                History
              </button>
            </>
          )}
          {mode === "edit" && canDelete && (
            <button
              type="button"
              className="btn-app-ghost is-danger"
              onClick={onDelete}
            >
              Delete
            </button>
          )}
          <button
            type="button"
            className="btn-app"
            onClick={onSave}
            disabled={create.isPending || update.isPending}
          >
            {create.isPending || update.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      {error && <p className="auth-error mb-4">— {error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        {/* Editor column */}
        <div className="flex flex-col gap-4">
          {showTemplatePicker ? (
            <TemplatePicker onPick={onPickTemplate} />
          ) : (
            <>
              <input
                className="auth-input"
                style={{ fontSize: "1.5rem", padding: "1rem 1.1rem", fontFamily: "var(--font-display)" }}
                placeholder="Title"
                value={meta.title}
                onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))}
              />
              <input
                className="auth-input"
                placeholder="Optional subtitle"
                value={meta.subtitle}
                onChange={(e) => setMeta((m) => ({ ...m, subtitle: e.target.value }))}
              />
              <BlockEditor
                doc={doc}
                onChange={setDoc}
                config={articleEditorConfig}
                editable={canEditThis}
                placeholder="An empty page. Click the + below to insert a block, or pick a template."
              />
            </>
          )}
        </div>

        {/* Sidebar */}
        <aside className="flex flex-col gap-5">
          <Field label="Status">
            <select
              className="auth-input"
              value={meta.status}
              onChange={(e) =>
                setMeta((m) => ({ ...m, status: e.target.value as MetaState["status"] }))
              }
            >
              {allowedStatuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
          {canPublish && (
            <Field
              label="Publish date"
              hint="Leave blank to use today's date when published. Editable to backdate or schedule."
            >
              <input
                className="auth-input"
                type="datetime-local"
                value={meta.publishedAt}
                onChange={(e) =>
                  setMeta((m) => ({ ...m, publishedAt: e.target.value }))
                }
              />
            </Field>
          )}
          <Field label="Visibility">
            <select
              className="auth-input"
              value={meta.visibility}
              onChange={(e) => setMeta((m) => ({ ...m, visibility: e.target.value as MetaState["visibility"] }))}
            >
              {VIS_OPTIONS.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </Field>
          <Field label="Category">
            <select
              className="auth-input"
              value={meta.category}
              onChange={(e) => setMeta((m) => ({ ...m, category: e.target.value as MetaState["category"] }))}
            >
              {CAT_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
          <Field label="Template">
            <select
              className="auth-input"
              value={meta.templateKey}
              onChange={(e) => setMeta((m) => ({ ...m, templateKey: e.target.value as MetaState["templateKey"] }))}
            >
              {TPL_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
          <Field label="Tags" hint="Comma-separated. Pick from existing or add new ones.">
            <input
              className="auth-input"
              type="text"
              placeholder="upanishad, satsang"
              value={meta.tags}
              onChange={(e) => setMeta((m) => ({ ...m, tags: e.target.value }))}
              list="tag-suggestions"
            />
            <datalist id="tag-suggestions">
              {tagSuggestions.map((t) => (
                <option key={t.tag} value={t.tag}>
                  {t.count}× used
                </option>
              ))}
            </datalist>
          </Field>
          <Field label="Cover image URL" hint="Optional.">
            <input
              className="auth-input"
              type="url"
              placeholder="https://"
              value={meta.coverImage}
              onChange={(e) => setMeta((m) => ({ ...m, coverImage: e.target.value }))}
            />
          </Field>
          <Field label="Excerpt" hint="Plain text shown in cards / search.">
            <textarea
              className="auth-input"
              rows={3}
              value={meta.excerpt}
              onChange={(e) => setMeta((m) => ({ ...m, excerpt: e.target.value }))}
            />
          </Field>
          {canFeature && (
            <Field label="Featured">
              <label
                className="auth-input flex items-center gap-3 cursor-pointer"
                style={{ background: "var(--color-vellum-dim)" }}
              >
                <input
                  type="checkbox"
                  checked={meta.featured}
                  onChange={(e) => setMeta((m) => ({ ...m, featured: e.target.checked }))}
                  style={{ width: "auto" }}
                />
                <span className="font-grotesk text-[0.85rem]">{meta.featured ? "On the landing strip" : "Off"}</span>
              </label>
            </Field>
          )}
          <Field label="Related event" hint="Optional. Links this article to an event in the calendar.">
            <select
              className="auth-input"
              value={meta.relatedEventId ?? ""}
              onChange={(e) =>
                setMeta((m) => ({
                  ...m,
                  relatedEventId: e.target.value ? Number(e.target.value) : null,
                }))
              }
            >
              <option value="">— None —</option>
              {eventOptions.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {format(new Date(ev.startsAt), "d LLL yyyy")} · {ev.title}
                </option>
              ))}
            </select>
          </Field>
          {mode === "edit" && (
            <Field label="Revision note" hint="Saved with this version.">
              <input
                className="auth-input"
                type="text"
                placeholder="Fixed typo in section 2"
                value={meta.revisionNote}
                onChange={(e) => setMeta((m) => ({ ...m, revisionNote: e.target.value }))}
              />
            </Field>
          )}
          {mode === "create" && (
            <button
              type="button"
              className="btn-app-ghost"
              onClick={() => setShowTemplatePicker(true)}
            >
              Pick a different template
            </button>
          )}
        </aside>
      </div>

      {/* Mobile sticky action bar */}
      <div className="md:hidden form-action-bar mt-8">
        <button type="button" className="btn-app-ghost" onClick={() => navigate(-1)}>
          Cancel
        </button>
        {mode === "edit" && (
          <button type="button" className="btn-app-ghost" onClick={onPreview}>
            Preview
          </button>
        )}
        <button
          type="button"
          className="btn-app"
          onClick={onSave}
          disabled={create.isPending || update.isPending}
        >
          {create.isPending || update.isPending ? "Saving…" : "Save"}
        </button>
      </div>

      <RevisionDrawer
        articleId={article?.id ?? null}
        open={showRevisions}
        onClose={() => setShowRevisions(false)}
        onRestore={onRestoreRevision}
      />
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="auth-field">
      <label className="auth-label">{label}</label>
      {children}
      {hint && <p className="auth-hint">{hint}</p>}
    </div>
  );
}

function TemplatePicker({ onPick }: { onPick: (key: typeof TPL_OPTIONS[number]) => void }) {
  return (
    <div>
      <p className="eyebrow mb-3">Choose a template</p>
      <h2 className="font-display text-[clamp(1.5rem,3.5vw,2.1rem)] tracking-tight mb-3">
        A pre-built starting page.
      </h2>
      <p className="text-[1rem] text-[var(--color-ink-soft)] mb-9 max-w-[60ch]">
        Each template drops a fully arranged article into the canvas — a
        heading, an opening, the right sections, a close. Edit, rearrange, and
        add blocks from there. You can also start from a blank page.
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <li>
          <button
            type="button"
            onClick={() => onPick("blank")}
            className="template-card template-card-blank"
          >
            <p className="font-grotesk text-[0.7rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-2">
              Blank
            </p>
            <p className="font-display text-[1.5rem] leading-[1.15] tracking-tight mb-3">
              Start from nothing.
            </p>
            <p className="text-[0.95rem] leading-[1.5] text-[var(--color-ink-soft)]">
              An empty page. Add blocks from the palette as you go.
            </p>
          </button>
        </li>
        {TEMPLATES.map((t) => (
          <li key={t.key}>
            <button type="button" onClick={() => onPick(t.key)} className="template-card">
              <div
                className="template-card-preview"
                dangerouslySetInnerHTML={{ __html: t.previewHtml }}
              />
              <div className="template-card-meta">
                <p className="font-grotesk text-[0.7rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-1.5">
                  {t.label}
                </p>
                <p className="text-[0.95rem] leading-[1.5] text-[var(--color-ink-soft)]">{t.blurb}</p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
